import {
  Box,
  Grid,
  GridItem,
  Radio,
  RadioGroup,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import Explanation from "../../components/Explanation";
import Control from "../../components/Control";
import { useAppDispatch } from "../../../store";
import { ModuleMode, showContinue } from "../../../store/slices/contentSlice";
import { useAudio } from "../../../Providers/AudioProvider";

import QuestionHeader from "../../components/QuizHeader";
import QuizXpPoint from "../../components/QuizXpPoint";
import QuizHint from "../../components/QuizHint";
// import Toaster from "../../components/Toaster";
import MarkDownRender from "../../MarkDownRender";
import PythonCodeEditor from "../PythonCodeEditor";
import CodeHighlight from "../codeHightlight";
import { IContentAPI } from "../../JsonRender";
import { ModuleType } from "../../../internal/courses/api";

interface QuizState {
  value: number | null;
  showCorrectOption: boolean;
  showExplanation: boolean;
  hint: string | null;
  feedback: string;
  disabledOptions: number[];
  showXpPoints: boolean;
  showTryAgainButton: boolean;
  showCheckButton: boolean;
  showResetButton: boolean;
  showHint: boolean;
  optionHightlight: "neutral" | "warning" | "success";
}

type InternalProps = {
  status: "completed" | "not-started";
  contentAPI: IContentAPI;
  meta: Record<any, any>;
  viewMode: ModuleMode;
  moduleType: ModuleType;
};

export type QuizProps = {
  type: "grid" | "list";
  header: IConfig[];
  question: IConfig[];
  options: IConfig[];
  explanation: IConfig[];
  hint: IConfig[];
  correctOption: number;
  xpPoints: number;
  showborders?: "boolean";
  footer: IConfig[];
  isOptionsIsCode?: boolean;
  language?: string;
} & InternalProps;

// Component
const Quiz = (props: QuizProps) => {
  const {
    type,
    question,
    explanation,
    options,
    status = "not-started",
    correctOption,
    contentAPI,
    xpPoints,
    meta,
    // hint,
    isOptionsIsCode = false,
    showborders = true,
    language,
    moduleType,
  } = props;
  const [quizState, setQuizState] = useState<QuizState>({
    value: null,
    showCorrectOption: false,
    disabledOptions: [],
    showExplanation: false,
    hint: null,
    feedback: "Incorrect, try again.",
    showHint: false,
    showTryAgainButton: false,
    showCheckButton: true,
    showXpPoints: false,
    showResetButton: false,
    optionHightlight: "neutral",
  });
  const toast = useToast();
  const { playWrongOption, playCorrectSound } = useAudio();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (meta && meta.showXpPoints) {
      setQuizState((p) => ({ ...p, showXpPoints: true }));
    }
  }, [meta]);

  useEffect(() => {
    if (status === "completed") {
      setQuizState((prev) => ({
        ...prev,
        value: correctOption,
        showCheckButton: false,
        showCorrectOption: true,
        disabledOptions: getDisabledOptions(correctOption, options.length),
      }));
    }
  }, [status, correctOption, options, type]);

  // Helper functions
  const getDisabledOptions = (
    correctOpt: number,
    totalOptions: number
  ): number[] => {
    return Array.from({ length: totalOptions }, (_, i) => i + 1).filter(
      (v) => v !== correctOpt
    );
  };

  const checkIsCorrectOption = () => {
    const isCorrect = quizState.value === correctOption;
    return isCorrect;
  };

  const contentAPIComplete = () => {
    contentAPI.completed({ showXpPoints: true });
  };

  const onCorrect = () => {
    setQuizState((prev) => ({
      ...prev,
      isSelectedCorrect: true,
      showCorrectOption: true,
      disabledOptions: getDisabledOptions(correctOption, options.length),
      showHint: false,
      showCheckButton: false,
      showXpPoints: true,
      optionHightlight: "success",
    }));
    contentAPIComplete();
    playCorrectSound(0.6);
  };

  const checkIsLastOption = () => {
    const selectedOption = Number(quizState.value);
    const newDisabledOptions = [...quizState.disabledOptions, selectedOption];
    const remainingOptions = options.length - newDisabledOptions.length;
    return remainingOptions === 1;
  };

  const onWrong = () => {
    const selectedOption = Number(quizState.value);
    const newDisabledOptions = [...quizState.disabledOptions, selectedOption];
    const isLastOption = checkIsLastOption();

    playWrongOption(0.9);

    setQuizState((prev) => ({
      ...prev,
      value: isLastOption ? correctOption : null,
      showCorrectOption: isLastOption,
      disabledOptions: newDisabledOptions,
      showCheckButton: false,
      showTryAgainButton: isLastOption ? false : true,
      showHint: isLastOption ? false : true,
      optionHightlight: isLastOption ? "warning" : "neutral",
    }));
    if (isLastOption) {
      contentAPI.answer_revealed();
      dispatch(showContinue());
    }
  };

  // const showToast = (feedback: string) => {
  //   toast({
  //     id: "quiz_feedback",
  //     description: feedback,
  //     status: "warning",
  //     duration: 9000,
  //     isClosable: true,
  //     render: (props) => (
  //       <Toaster onClose={props.onClose} feedback={feedback} />
  //     ),
  //   });
  // };

  const handleCheck = () => {
    const isCorrect = checkIsCorrectOption();
    if (isCorrect) {
      onCorrect();
    } else {
      onWrong();
    }
  };

  const handleExplanation = () => {
    playWrongOption(0.9);
    if (!quizState.showCorrectOption && options) {
      setQuizState((prev) => ({
        ...prev,
        showCorrectOption: true,
        value: correctOption,
        showHint: false,
        disabledOptions: getDisabledOptions(correctOption, options.length),
        optionHightlight: "warning",
        showCheckButton: false,
        showTryAgainButton: false,
      }));
    }
    // save the event of explanaiton
    if (
      !quizState.showExplanation &&
      !quizState.showCorrectOption &&
      status !== "completed"
    ) {
      contentAPI.explanation_viewed();
    }

    setQuizState((prev) => ({
      ...prev,
      showExplanation: !prev.showExplanation,
    }));
  };

  const handleReset = () => {
    setQuizState({
      value: null,
      disabledOptions: [],
      showCorrectOption: false,
      showExplanation: false,
      hint: quizState.hint,
      feedback: "Incorrect, try again.",
      showTryAgainButton: false,
      showHint: false,
      showCheckButton: true,
      showXpPoints: false,
      optionHightlight: "neutral",
      showResetButton: false,
    });
  };

  const handleTryAgain = () => {
    setQuizState((p) => ({
      ...p,
      showCheckButton: true,
      showTryAgainButton: false,
    }));
  };

  const handleQuizClick = () => {};

  const handleOnChange = (value: number) => {
    toast.closeAll();
    if (
      !quizState.disabledOptions.includes(value) &&
      !quizState.showCorrectOption
    ) {
      setQuizState((prev) => ({
        ...prev,
        value,
        showCheckButton: true,
        showTryAgainButton: false,
      }));
    }
  };

  const renderRadioOptions = () => (
    <RadioGroup
      my="1rem"
      variant={
        quizState.showCorrectOption
          ? quizState.optionHightlight == "success"
            ? "success"
            : "warning"
          : "primary"
      }
      {...(quizState.value ? { value: `${quizState.value}` } : {})}
      px={type === "grid" ? "2rem" : "0.7rem"}
    >
      <Grid
        gap={type === "list" ? ".4rem" : "1rem"}
        flexDir="column"
        gridTemplateColumns={type === "list" ? "1fr" : "1fr 1fr"}
      >
        {options &&
          options.map((opt, index) => (
            <GridItem
              key={index}
              cursor={
                quizState.disabledOptions.includes(index)
                  ? "not-allowed"
                  : "pointer"
              }
              opacity={quizState.disabledOptions.includes(index) ? 0.5 : 1}
              onClick={() => handleOnChange(index)}
            >
              <Radio
                minH="3rem"
                py={".6rem"}
                size={type === "grid" ? "md" : "lg"}
                showborders={showborders ? "true" : `${type === "grid"}`}
                value={`${index}`}
                isDisabled={quizState.disabledOptions.includes(index)}
              >
                {opt.component === "Markdown" &&
                  (isOptionsIsCode ? (
                    <CodeHighlight
                      code={opt.props.content}
                      language={(language as any) ?? "python"}
                    />
                  ) : (
                    <Box pl={"1rem"} pt={"0.2rem"}>
                      <MarkDownRender content={opt.props.content} />
                    </Box>
                  ))}
                {opt.component === "PythonCodeEditor" && (
                  <PythonCodeEditor {...opt.props} mode="view" />
                )}
              </Radio>
            </GridItem>
          ))}
      </Grid>
    </RadioGroup>
  );

  return (
    <Box
      px={{ base: "1rem", lg: "none" }}
      width={{ base: "100%", lg: "31rem" }}
      mx="auto"
      onClick={handleQuizClick}
    >
      <Box
        border="1px solid"
        borderRadius="1rem"
        borderColor={
          moduleType === ModuleType.content ? "neutral.800" : "transparent"
        }
        width="100%"
        py="1rem"
      >
        <Box px="1.5rem">
          <QuestionHeader question={question} />
        </Box>

        {renderRadioOptions()}

        {/* Explanation Section */}
        <Explanation
          explanantion={explanation}
          showExplanation={quizState.showExplanation}
        />

        {/* Controls */}
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          px="1rem"
        >
          <Control
            showCheck={quizState.showCheckButton}
            isCheckDisabled={!`${quizState.value}`}
            showTryAgain={quizState.showTryAgainButton}
            showExplanation={quizState.showExplanation}
            showReset={quizState.showResetButton}
            explanantionOnClick={handleExplanation}
            onCheckClick={handleCheck}
            resetOnClick={handleReset}
            onTryAgainClick={handleTryAgain}
          />

          <QuizXpPoint
            showXpPoints={quizState.showXpPoints}
            xpPoints={xpPoints}
          />
        </Box>
      </Box>
      <QuizHint
        hint={quizState.hint}
        isHintShowed={quizState.showHint}
        onClick={() => {
          setQuizState((p) => ({ ...p, showHint: false }));
          toast.closeAll();
        }}
      />
    </Box>
  );
};

export default Quiz;
